// ============================================================================
// AFB-0075 / PR 3 — "who actually gets what", for one packet and one person.
//
// The packet editor above shows eight documents with three mutually exclusive
// state forms among them. Nobody receives that list. What a person receives is
// SIX documents in a particular order starting on particular pages, and the
// only way to know the rules are right is to see that answer for a real
// combination before anybody is sent one.
//
// The state selector starts at NOT SET and stays there until somebody chooses,
// because that is the true state of 8 of the 26 people on file. A blank state
// selects exactly the same documents a Texas hire gets — five, 22 pages — so
// the list CANNOT show the difference between "no form applies" and "we don't
// know where they live". That is why the gap is a banner and not a footnote:
// the wrong one of those two is a person onboarded with no withholding form.
//
// All of the arithmetic is in ./packet-composition, which is pure and tested.
// This file only renders it.
// ============================================================================

import { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { EsignTemplate } from './envelope-lib';
import {
  PacketDocumentRow, PacketDocumentTemplate, composePacket, describePageSpan,
} from './packet-lib';

const INPUT =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-xs text-tcs-text';
const LABEL = 'block text-[11px] font-medium text-tcs-text-secondary';

/**
 * employees.employment_type holds only these two values live (verified in
 * hr-hiring). 'Partner' exists in the checklist vocabulary and can be TICKED on
 * a document row, but no employee record carries it, so offering it here would
 * preview a person who does not exist.
 */
const EMPLOYMENT_TYPES = ['W2', 'Subcontractor'];

/**
 * The states worth offering: the ones TCS operates in, plus every state this
 * packet actually names. Any state on neither list behaves identically to every
 * other unnamed state — fifty options that all give the same answer would hide
 * the three that do not.
 */
const OPERATING_STATES = ['CO', 'FL', 'KS', 'MO', 'NV', 'TX'];

export interface PacketPreviewProps {
  packetName: string;
  documents: PacketDocumentRow[];
  templates: EsignTemplate[];
}

export function PacketPreview({ packetName, documents, templates }: PacketPreviewProps) {
  const [employmentType, setEmploymentType] = useState(EMPLOYMENT_TYPES[0]);
  const [state, setState] = useState('');

  const members: PacketDocumentTemplate[] = useMemo(
    () => templates.map((t) => ({
      template_id: t.template_id,
      name: t.name,
      page_count: t.page_count,
      document_bytes: t.document_bytes,
    })),
    [templates],
  );

  const states = useMemo(() => {
    const named = documents.flatMap((d) => d.applies_to_states ?? []);
    return Array.from(new Set([...OPERATING_STATES, ...named])).sort();
  }, [documents]);

  const composed = useMemo(
    () => composePacket(documents, members, { employment_type: employmentType, state }),
    [documents, members, employmentType, state],
  );

  const count = composed.documents.length;

  return (
    <div className="border-t border-tcs-border pt-2">
      <p className="text-[11px] font-semibold text-tcs-text">
        What one person actually receives
      </p>
      <p className="text-[11px] text-tcs-text-muted">
        Pick a person's details and see the packet they would be sent — which
        documents, in what order, starting on which page.
      </p>

      <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
        <div>
          <label className={LABEL} htmlFor="preview-employment-type">Employment type</label>
          <select
            id="preview-employment-type"
            value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value)}
            className={`mt-1 ${INPUT}`}
          >
            {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={LABEL} htmlFor="preview-state">State</label>
          <select
            id="preview-state"
            value={state}
            onChange={(e) => setState(e.target.value)}
            className={`mt-1 ${INPUT}`}
          >
            <option value="">Not set — we don't know where they live</option>
            {states.map((s) => (
              <option key={s} value={s}>
                {s}
                {documents.some((d) => (d.applies_to_states ?? []).includes(s))
                  ? ''
                  : ' — no form on this packet'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* The gap. Loud, because the list below cannot show it. */}
      {composed.state_gap && (
        <div
          role="alert"
          className="mt-2 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-2"
        >
          <p className="flex items-start gap-1.5 text-[11px] font-semibold text-st-warn-fg-strong">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            State unknown — this preview is not what they should be sent
          </p>
          <p className="mt-1 text-[11px] text-st-warn-fg-strong">
            {composed.state_gap.message}
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px] text-st-warn-fg-strong">
            {composed.state_gap.skipped.map((s) => (
              <li key={s.template_id}>
                {s.name} — only for {s.states.join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* An unknown page count is a refusal. It is not a warning, because the
          packet cannot be assembled at all until it is fixed. */}
      {!composed.assemblable && (
        <div
          role="alert"
          className="mt-2 rounded-md border border-st-danger-border-strong bg-st-danger-bg p-2"
        >
          <p className="text-[11px] font-semibold text-st-danger-fg">
            This packet cannot be assembled
          </p>
          <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-[11px] text-st-danger-fg">
            {composed.blockers.map((b) => <li key={b}>{b}</li>)}
          </ul>
        </div>
      )}

      <p className="mt-2 text-[11px] text-tcs-text">
        <strong>
          {count} document{count === 1 ? '' : 's'}
          {composed.total_pages === null
            ? ' · total pages unknown'
            : ` · ${composed.total_pages} page${composed.total_pages === 1 ? '' : 's'}`}
        </strong>{' '}
        <span className="text-tcs-text-muted">
          from “{packetName}” for a {employmentType} in {state === '' ? 'an unknown state' : state}
        </span>
      </p>

      {count === 0 ? (
        <p className="mt-1 text-[11px] italic text-tcs-text-muted">
          Nothing on this packet applies to this combination, so this person
          would receive an empty envelope. Check the “who gets it” ticks above.
        </p>
      ) : (
        <ol className="mt-1 space-y-1">
          {composed.documents.map((doc, i) => (
            <li
              key={doc.link_id}
              className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1 text-[11px]"
            >
              <span className="font-medium text-tcs-text">
                {i + 1}. {doc.name}
              </span>{' '}
              <span className={doc.blocker ? 'text-st-danger-fg' : 'text-tcs-text-muted'}>
                · {describePageSpan(doc)}
                {composed.total_pages !== null && doc.page_offset !== null
                  ? ` of ${composed.total_pages}`
                  : ''}
              </span>
              {!doc.is_required && (
                <span className="text-tcs-text-muted"> · optional</span>
              )}
              {doc.states && (
                <span className="text-tcs-text-secondary"> · {doc.states.join('/')} only</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
